import React, { useEffect, useRef, useState } from "react";
import { stripRichMarkers } from "@/lib/richFormat";
import { toPng } from "html-to-image";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { shareImageOrLink } from "@/lib/nativeShare";
import { shareLinkFor } from "@/lib/shareLinks";

const CAT_LABEL = (t, key) => ({
  healing: t("testimony.cat_healing", "Guérison"),
  provision: t("testimony.cat_provision", "Provision"),
  salvation: t("testimony.cat_salvation", "Salut"),
  family: t("testimony.cat_family", "Famille"),
  other: t("testimony.cat_other", "Autre"),
}[key] || "");

const initialsOf = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// Fetch an image and return a same-origin data URL (avoids canvas taint); null on failure.
const toDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
};

// Generates a branded, shareable image of a testimony (WhatsApp / Instagram / download).
export const ShareTestimony = ({ post, open, onOpenChange }) => {
  const { t } = useApp();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [avatarData, setAvatarData] = useState(null);
  const author = post.author || {};

  useEffect(() => {
    if (!open) return;
    setAvatarData(null);
    toDataUrl(avatarSrc(author)).then(setAvatarData);
  }, [open]); // eslint-disable-line

  const buildPng = async () => {
    const node = cardRef.current;
    return await toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#2D5A4C" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await buildPng();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `temoignage-yalola-${post.id}.png`;
      a.click();
      toast.success(t("share.downloaded", "Image téléchargée — partage-la sur WhatsApp/Instagram ✨"));
    } catch { toast.error(t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      await shareImageOrLink({
        title: t("testimony.title", "Témoignage"),
        text: t("share.text", "Un témoignage à découvrir sur Yalola 🙏"),
        url: shareLinkFor("post", post.id),
        copiedMessage: t("share.copied", "Copié — collez où vous voulez"),
      });
    } catch { toast.error(t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-testimony-dialog">
        <DialogHeader>
          <DialogTitle>{t("share.title", "Partager ce témoignage")}</DialogTitle>
          <DialogDescription className="sr-only">{t("testimony.share_sr", "Génère une image à partager sur les réseaux")}</DialogDescription>
        </DialogHeader>

        {/* Preview / capture target */}
        <div className="flex justify-center">
          <div
            ref={cardRef}
            style={{
              width: 320, minHeight: 400, padding: 28, borderRadius: 20, position: "relative",
              background: "linear-gradient(150deg, #2D5A4C 0%, #1f4438 55%, #D97757 160%)",
              color: "#fff", fontFamily: "system-ui, sans-serif", overflow: "hidden",
            }}
            data-testid="share-testimony-card"
          >
            <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, borderRadius: 999, background: "rgba(255,255,255,0.06)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, letterSpacing: 1, fontSize: 13, textTransform: "uppercase", opacity: 0.95 }}>
              ✝ Yalola
            </div>
            {post.testimony_category && (
              <div style={{ display: "inline-block", marginTop: 14, padding: "4px 12px", borderRadius: 999, background: "rgba(255,255,255,0.18)", fontSize: 12, fontWeight: 700 }}>
                {CAT_LABEL(t, post.testimony_category)}
              </div>
            )}
            <div style={{ marginTop: 16, fontSize: 20, fontWeight: 700, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 8, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              “{stripRichMarkers(post.content || "").trim()}”
            </div>
            <div style={{ position: "absolute", left: 28, right: 28, bottom: 22, display: "flex", alignItems: "center", gap: 10 }}>
              {avatarData ? (
                <img src={avatarData} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover", border: "2px solid rgba(255,255,255,0.5)" }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, background: "rgba(255,255,255,0.22)", border: "2px solid rgba(255,255,255,0.5)" }}>
                  {initialsOf(author.name)}
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{author.name || t("testimony.a_member", "Un membre")}</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>{t("testimony.card_footer", "yalola.app · Témoignage")}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 mt-3">
          <Button className="pill-primary" onClick={share} disabled={busy} data-testid="share-testimony-share"><Share2 size={16} className="mr-2" /> {t("share.share", "Partager")}</Button>
          <Button variant="outline" onClick={download} disabled={busy} data-testid="share-testimony-download"><Download size={16} className="mr-2" /> {t("share.download", "Télécharger")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareTestimony;
